<script setup lang="ts">
import useLocale from '../../composables/useLocale';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faGear as faGearSolid } from '@fortawesome/pro-solid-svg-icons';
import { faGear } from '@fortawesome/pro-regular-svg-icons';
import { storeToRefs } from 'pinia';
import useReaderStore from '../../stores/reader';
import { computed, ref } from 'vue';
import { FontAwesomeIconSizes } from '../../types';
import { useDisplay } from 'vuetify';

library.add(faGear, faGearSolid);

type Props = {
  hasSettings: boolean;
  isSettingsVisible: boolean;
  showTooltips?: boolean;
  btnSize?: number;
  iconSize?: FontAwesomeIconSizes;
  [key: string]: any; // Pass through any other props
};
const props = withDefaults(defineProps<Props>(), {
  showTooltips: true,
  btnSize: 48,
  iconSize: 'xl',
});

const { t } = useLocale();
const { isAudioOnly } = storeToRefs(useReaderStore());
const { smAndDown, sm } = useDisplay();

const icon = computed(() => (isAudioOnly.value ? 'far fa-gear' : 'fas fa-gear'));

const settingsBtn = ref();

defineExpose({
  settingsBtn,
});
</script>

<template>
  <v-btn
    ref="settingsBtn"
    data-testid="bp-settings-btn"
    color="on-brand"
    v-bind="{
      ...props,
    }"
    variant="text"
    :ripple="false"
    :icon="!isAudioOnly"
    :disabled="!hasSettings"
    :aria-label="t('button_settings')"
    :title="t('button_settings')"
    :height="btnSize"
    :width="isAudioOnly && !smAndDown ? '190' : isAudioOnly && sm ? '120' : btnSize"
    :slim="isAudioOnly && smAndDown"
  >
    <div :class="{ 'd-flex flex-column flex-md-row align-center ga-2 overflow-hidden': isAudioOnly }">
      <FontAwesomeIcon :class="{ 'rotate-icon': isSettingsVisible }" :icon="icon" :size="iconSize" />
      <span v-if="isAudioOnly" class="text-truncate" :class="smAndDown ? 'text-body-2 w-100' : 'text-body-1'">{{
        t('button_settings')
      }}</span>
    </div>
    <v-tooltip :disabled="!showTooltips" activator="parent" location="top" :text="t('button_settings')" />
  </v-btn>
</template>

<style scoped lang="scss">
.v-icon {
  transition: transform 0.3s ease;
}

.rotate-icon {
  transform: rotate(45deg);
}
</style>
